import React from "react";
import { Dialog, DialogTitle, DialogContent, DialogActions } from "@mui/material";

// import SAME dropdown options used in RegisterCard
import {
  GENDER_OPTIONS,
  MOTHER_TONGUE_OPTIONS,
  MARITAL_STATUS_OPTIONS,
  CHILDREN_OPTIONS,
  KIDS_LIVE_WITH_OPTIONS,
  RELIGION_OPTIONS,
  CASTE_OPTIONS,
  ZODIAC_OPTIONS,
  STAR_OPTIONS,
  BODY_TYPE_OPTIONS,
  PHYSICAL_STATUS_OPTIONS,
  HEIGHT_OPTIONS,
  WEIGHT_OPTIONS,
  COMPLEXION_OPTIONS,
  COUNTRY_OPTIONS,
  RESIDENT_STATUS_OPTIONS,
  SEVVAI_LAKNA_OPTIONS,
  PROFILE_CREATED_BY_OPTIONS,
  STAR_PATHAM_OPTIONS,
  EATING_HABIT_OPTIONS,
  DRINKING_HABIT_OPTIONS,
  SMOKING_HABIT_OPTIONS,
} from "../../../utils/selectOptions.js";

type Option = { value: string; label: string };

type FieldProps = {
  editData: Record<string, any>;
  onChange: (field: string, value: string) => void;
};

type InputProps = FieldProps & {
  label: string;
  field: string;
  type?: string;
};

type SelectProps = FieldProps & {
  label: string;
  field: string;
  options: Option[];
  placeholder?: string;
};

const Input = ({ editData, onChange, label, field, type = "text" }: InputProps) => (
  <div className='input-group'>
    <label>{label}</label>
    <input type={type} value={editData?.[field] || ""} onChange={(event) => onChange(field, event.target.value)} />
  </div>
);

const Select = ({ editData, onChange, label, field, options, placeholder = "Select" }: SelectProps) => (
  <div className='input-group'>
    <label>{label}</label>
    <select value={editData?.[field] || ""} onChange={(event) => onChange(field, event.target.value)}>
      <option value=''>{placeholder}</option>
      {options.map((option) => (
        <option key={option.value} value={option.value}>
          {option.label}
        </option>
      ))}
    </select>
  </div>
);

type EditProfileDialogProps = {
  open: boolean;
  onClose: () => void;
  editData: Record<string, any>;
  setEditData: React.Dispatch<React.SetStateAction<Record<string, any>>>;
  onSave: () => Promise<void>;
  saving: boolean;
};

const EditProfileDialog = ({ open, onClose, editData, setEditData, onSave, saving }: EditProfileDialogProps) => {
  const handleChange = (field: string, value: string) => {
    if (field === "dob" && value) {
      const birthDate = new Date(`${value}T00:00:00`);
      const today = new Date();
      let age = today.getFullYear() - birthDate.getFullYear();
      const monthDiff = today.getMonth() - birthDate.getMonth();

      if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
        age -= 1;
      }

      if (age < 18) {
        setEditData((previous) => ({
          ...previous,
          dob: "",
        }));
        return;
      }
    }

    setEditData((previous) => ({ ...previous, [field]: value }));
  };

  const fieldProps = { editData, onChange: handleChange };

  return (
    <Dialog open={open} onClose={onClose} maxWidth='lg' fullWidth>
      <DialogTitle>
        <h3>Edit Full Profile</h3>
      </DialogTitle>

      <DialogContent>
        <div className='premium-form'>
          {/* ================= BASIC ================= */}
          <h3 className='section-title'>Basic Information</h3>
          <div className='form-grid'>
            <Input {...fieldProps} label='Full Name' field='fullName' />
            <Input {...fieldProps} label='Mobile' field='mobile' />
            <Input {...fieldProps} label='Date of Birth' field='dob' type='date' />
            <Input {...fieldProps} label='Birth Time' field='birth_time' type='time' />

            <Select {...fieldProps} label='Gender' field='gender' options={GENDER_OPTIONS} />
          </div>

          {/* ================= PERSONAL ================= */}
          <h3 className='section-title'>Personal Details</h3>
          <div className='form-grid'>
            <Select {...fieldProps} label='Mother Tongue' field='mother_tongue' options={MOTHER_TONGUE_OPTIONS} />
            <Select {...fieldProps} label='Profile Created By' field='profile_created_by' options={PROFILE_CREATED_BY_OPTIONS} />

            <Select {...fieldProps} label='Marital Status' field='marital_status' options={MARITAL_STATUS_OPTIONS} />
            <Select {...fieldProps} label='Children' field='children' options={CHILDREN_OPTIONS} />
            <Select {...fieldProps} label='Kids Live With' field='kids_live_with' options={KIDS_LIVE_WITH_OPTIONS} />

            <Select {...fieldProps} label='Religion' field='religion' options={RELIGION_OPTIONS} />
            <Select {...fieldProps} label='Caste' field='caste' options={CASTE_OPTIONS} />

            <Select {...fieldProps} label='Zodiac' field='zodiac' options={ZODIAC_OPTIONS} />
            <Select {...fieldProps} label='Star' field='star' options={STAR_OPTIONS} />
            <Select {...fieldProps} label='Star Patham' field='star_patham' options={STAR_PATHAM_OPTIONS} />
          </div>

          {/* ================= PHYSICAL ================= */}
          <h3 className='section-title'>Physical Information</h3>
          <div className='form-grid'>
            <Select {...fieldProps} label='Body Type' field='body_type' options={BODY_TYPE_OPTIONS} />
            <Select {...fieldProps} label='Physical Status' field='physical_status' options={PHYSICAL_STATUS_OPTIONS} />

            <Select {...fieldProps} label='Height' field='height' options={HEIGHT_OPTIONS} />
            <Select {...fieldProps} label='Weight' field='weight' options={WEIGHT_OPTIONS} />

            <Select {...fieldProps} label='Complexion' field='complexion' options={COMPLEXION_OPTIONS} />
          </div>

          {/* ================= LOCATION ================= */}
          <h3 className='section-title'>Location</h3>
          <div className='form-grid'>
            <Select {...fieldProps} label='Birth Country' field='birth_country' options={COUNTRY_OPTIONS} />
            <Select {...fieldProps} label='Resident Country' field='resident_country' options={COUNTRY_OPTIONS} />
            <Select {...fieldProps} label='Resident Status' field='resident_status' options={RESIDENT_STATUS_OPTIONS} />

            <Input {...fieldProps} label='City' field='city' />
            <Input {...fieldProps} label='Address' field='address' />
          </div>

          {/* ================= CAREER ================= */}
          <h3 className='section-title'>Career</h3>
          <div className='form-grid'>
            <Input {...fieldProps} label='Education' field='education' />
            <Input {...fieldProps} label='Occupation' field='occupation' />
          </div>

          {/* ================= FAMILY ================= */}
          <h3 className='section-title'>Family Details</h3>
          <div className='form-grid'>
            <Input {...fieldProps} label='Father Place' field='father_place' />
            <Input {...fieldProps} label='Mother Place' field='mother_place' />

            <Select {...fieldProps} label='Father Caste' field='father_caste' options={CASTE_OPTIONS} />
            <Select {...fieldProps} label='Mother Caste' field='mother_caste' options={CASTE_OPTIONS} />

            <Input {...fieldProps} label='Father Occupation' field='father_occupation' />
            <Input {...fieldProps} label='Mother Occupation' field='mother_occupation' />
          </div>

          {/* ================= LIFESTYLE ================= */}
          <h3 className='section-title'>Lifestyle</h3>
          <div className='form-grid'>
            <Input {...fieldProps} label='Hobbies' field='hobbies' />
            <Select {...fieldProps} label='Eating Habit' field='eating_habit' options={EATING_HABIT_OPTIONS} />
            <Select {...fieldProps} label='Drinking Habit' field='drinking_habit' options={DRINKING_HABIT_OPTIONS} />
            <Select {...fieldProps} label='Smoking Habit' field='smoking_habit' options={SMOKING_HABIT_OPTIONS} />
          </div>

          {/* ================= ASTROLOGY ================= */}
          <h3 className='section-title'>Astrology</h3>
          <div className='form-grid'>
            <Input {...fieldProps} label='Kiraga Paavam' field='kiraga_paavam' />
            <Select {...fieldProps} label='Sevvai Lakna' field='sevvai_lakna' options={SEVVAI_LAKNA_OPTIONS} />
          </div>

          {/* ================= ABOUT ================= */}
          <h3 className='section-title'>About</h3>

          <div className='input-group full-width'>
            <label>About Me</label>
            <textarea rows={4} value={editData?.about || ""} onChange={(e) => handleChange("about", e.target.value)} />
          </div>

          <div className='input-group full-width'>
            <label>Partner Preferences</label>
            <textarea
              rows={4}
              value={editData?.partner_qualities || ""}
              onChange={(e) => handleChange("partner_qualities", e.target.value)}
            />
          </div>
          {editData?.photo && (
            <img
              src={editData.photo}
              alt='Profile'
              style={{
                width: 120,
                height: 120,
                objectFit: "cover",
                borderRadius: 12,
                marginBottom: 10,
              }}
            />
          )}

          <div className='input-group'>
            <label>Profile Photo</label>
            <input
              type='file'
              accept='image/jpeg,image/png,image/webp,image/heic,image/heif'
              onChange={(e) =>
                setEditData((previous) => ({
                  ...previous,
                  photo: e.target.files?.[0] || null,
                }))
              }
            />
          </div>
          {editData?.horoscope && (
            <img
              src={editData.horoscope}
              alt='Horoscope'
              style={{
                width: 120,
                borderRadius: 12,
                marginBottom: 10,
              }}
            />
          )}
          <div className='input-group'>
            <label>Horoscope</label>
            <input
              type='file'
              accept='image/jpeg,image/png,image/webp,image/heic,image/heif'
              onChange={(e) =>
                setEditData((previous) => ({
                  ...previous,
                  horoscope: e.target.files?.[0] || null,
                }))
              }
            />
          </div>
        </div>
      </DialogContent>

      <DialogActions>
        <button className='btn-outline' onClick={onClose} disabled={saving}>
          Cancel
        </button>

        <button className='btn-primary' onClick={onSave} disabled={saving}>
          {saving ? "Saving..." : "Save Changes"}
        </button>
      </DialogActions>
    </Dialog>
  );
};

export default EditProfileDialog;
